import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { parseApiError } from "@/lib/api-error";
import { supabase } from "@/integrations/supabase/client";
import { createSearchJob } from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/contexts/CreditsContext";
import type {
  EmployeeSizeRange,
  SearchRequest,
  GewerbegebietZone,
} from "@/types/api";
import { Radar } from "lucide-react";

import { WizardStepper } from "./WizardStepper";
import { DescribeStep } from "./DescribeStep";
import { IndustryStep } from "./IndustryStep";
import { LocationStep } from "./LocationStep";
import { KeywordsStep } from "./KeywordsStep";
import { SettingsStep } from "./SettingsStep";
import { ReviewStep } from "./ReviewStep";

export function SearchWizard() {
  const { t } = useTranslation("search");
  const navigate = useNavigate();
  const navLocation = useLocation();
  const { profile: authProfile } = useAuth();
  const { refreshCredits } = useCredits();

  // ── Wizard navigation ──────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(1);

  // ── Step 1: Describe ───────────────────────────────────────────────────
  const [aiDescription, setAiDescription] = useState("");
  const [isEnriched, setIsEnriched] = useState(false);

  // ── Step 3: Industry ───────────────────────────────────────────────────
  const [industry, setIndustry] = useState("");
  const [employeeSize, setEmployeeSize] = useState<EmployeeSizeRange>("any");

  // ── Step 4: Location ───────────────────────────────────────────────────
  const [location, setLocation] = useState("");
  const [searchRadius, setSearchRadius] = useState(50);
  const [searchMode, setSearchMode] = useState<"radius" | "zones">("radius");
  const [selectedZones, setSelectedZones] = useState<GewerbegebietZone[]>([]);
  const [mapCenter, setMapCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // ── Step 5: Keywords ───────────────────────────────────────────────────
  const [keywords, setKeywords] = useState<string[]>([]);
  const [excludeKeywords, setExcludeKeywords] = useState<string[]>([]);

  // ── Step 6: Settings ───────────────────────────────────────────────────
  const [maxResults, setMaxResults] = useState(10);
  const [language, setLanguage] = useState("de");
  const [countryCode, setCountryCode] = useState("de");

  // ── Submission ─────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);

  // ── AI fill tracking ───────────────────────────────────────────────────
  const [aiSuggested, setAiSuggested] = useState(false);

  // ── Shared param applicator (used by prefill + AI fill) ───────────────
  function applySearchParams(p: Partial<SearchRequest>) {
    if (p.industry) setIndustry(p.industry);
    if (p.employee_size) setEmployeeSize(p.employee_size as EmployeeSizeRange);
    if (p.location) setLocation(p.location);
    if (p.search_radius_km)
      setSearchRadius(Math.max(1, Math.min(500, p.search_radius_km)));
    if (p.keywords?.length) setKeywords(p.keywords);
    if (p.exclude_keywords?.length) setExcludeKeywords(p.exclude_keywords);
    if (p.max_results) setMaxResults(Math.max(1, Math.min(200, p.max_results)));
    if (p.language) setLanguage(p.language);
    if (p.country_code) setCountryCode(p.country_code);
  }

  // ── "Run Again" prefill ────────────────────────────────────────────────
  useEffect(() => {
    const prefill = (navLocation.state as Record<string, unknown>)?.prefill as
      | SearchRequest
      | undefined;
    if (!prefill) return;
    applySearchParams(prefill);
    goToStep(6);
    window.history.replaceState({}, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navLocation.state]);

  // ── Navigation ─────────────────────────────────────────────────────────
  function goToStep(n: number) {
    setCurrentStep(n);
    window.scrollTo({ top: 0, behavior: "instant" });
  }

  // ── AI: Enrich description ─────────────────────────────────────────────
  async function handleEnrich() {
    if (!aiDescription.trim()) return;
    try {
      const { data, error } = await supabase.functions.invoke(
        "enrich-description",
        {
          body: { description: aiDescription },
        },
      );
      if (error) throw error;
      if (!data?.enriched) {
        toast.error(t("wizard.toasts.enrichNoResult"));
        return;
      }
      setAiDescription(data.enriched);
      toast.success(t("wizard.toasts.enrichSuccess"));
    } catch (err: unknown) {
      const { message } = parseApiError(err);
      toast.error(message);
    }
  }

  // ── AI: Fill form from description ────────────────────────────────────
  async function handleFillForm() {
    if (!aiDescription.trim()) return;
    try {
      const { data, error } = await supabase.functions.invoke("refine-search", {
        body: { description: aiDescription },
      });
      if (error) throw error;
      if (!data?.params) {
        toast.error(t("wizard.toasts.fillFormNoResult"));
        return;
      }
      applySearchParams(data.params);
      setAiSuggested(true);
      goToStep(2);
      toast.success(t("wizard.toasts.fillFormSuccess"));
    } catch (err: unknown) {
      const { message } = parseApiError(err);
      toast.error(message);
    }
  }

  // ── Submit ─────────────────────────────────────────────────────────────
  async function handleSubmit() {
    setSubmitting(true);
    const req: SearchRequest = {
      industry: industry || undefined,
      employee_size: employeeSize !== "any" ? employeeSize : undefined,
      location: location || undefined,
      search_radius_km:
        searchMode === "zones"
          ? undefined
          : location
            ? searchRadius
            : undefined,
      zone_bounds:
        searchMode === "zones" && selectedZones.length > 0
          ? selectedZones.map((z) => ({
              south: z.bounds.south,
              west: z.bounds.west,
              north: z.bounds.north,
              east: z.bounds.east,
              name: z.name,
              osm_id: z.osm_id,
            }))
          : undefined,
      keywords: keywords.length > 0 ? keywords : undefined,
      exclude_keywords:
        excludeKeywords.length > 0 ? excludeKeywords : undefined,
      max_results: maxResults,
      language,
      country_code: countryCode,
    };
    try {
      const job = await createSearchJob(req);
      refreshCredits();
      toast.success(t("wizard.toasts.jobStarted", { jobId: job.job_id }));
      navigate(`/jobs/${job.job_id}/results`);
    } catch (err: unknown) {
      const { message } = parseApiError(err);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="w-full max-w-[960px] mx-auto">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <Radar className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("wizard.header.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(`wizard.subtitles.${currentStep}`)}
          </p>
        </div>
      </div>

      {/* Stepper */}
      <WizardStepper currentStep={currentStep} onStepClick={goToStep} />

      {/* Step panels */}
      <div className="mt-6">
        {currentStep === 1 && (
          <DescribeStep
            description={aiDescription}
            onDescriptionChange={setAiDescription}
            isEnriched={isEnriched}
            onEnrichedChange={setIsEnriched}
            onEnrich={handleEnrich}
            onFillForm={handleFillForm}
          />
        )}

        {currentStep === 2 && (
          <IndustryStep
            description={aiDescription}
            industry={industry}
            onIndustryChange={setIndustry}
            employeeSize={employeeSize}
            onEmployeeSizeChange={setEmployeeSize}
            aiSuggested={aiSuggested}
            onBack={() => goToStep(1)}
            onContinue={() => goToStep(3)}
            onSkipToReview={() => goToStep(6)}
          />
        )}

        {currentStep === 3 && (
          <LocationStep
            description={aiDescription}
            location={location}
            onLocationChange={setLocation}
            searchRadius={searchRadius}
            onSearchRadiusChange={setSearchRadius}
            searchMode={searchMode}
            onSearchModeChange={setSearchMode}
            selectedZones={selectedZones}
            onSelectedZonesChange={setSelectedZones}
            mapCenter={mapCenter}
            onMapCenterChange={setMapCenter}
            onBack={() => goToStep(2)}
            onContinue={() => goToStep(4)}
            onSkipToReview={() => goToStep(6)}
          />
        )}

        {currentStep === 4 && (
          <KeywordsStep
            description={aiDescription}
            keywords={keywords}
            onKeywordsChange={setKeywords}
            excludeKeywords={excludeKeywords}
            onExcludeKeywordsChange={setExcludeKeywords}
            onBack={() => goToStep(3)}
            onContinue={() => goToStep(5)}
            onSkipToReview={() => goToStep(6)}
          />
        )}

        {currentStep === 5 && (
          <SettingsStep
            description={aiDescription}
            maxResults={maxResults}
            onMaxResultsChange={setMaxResults}
            language={language}
            onLanguageChange={setLanguage}
            countryCode={countryCode}
            onCountryCodeChange={setCountryCode}
            onBack={() => goToStep(4)}
            onContinue={() => goToStep(6)}
            onSkipToReview={() => goToStep(6)}
          />
        )}

        {currentStep === 6 && (
          <ReviewStep
            industry={industry}
            employeeSize={employeeSize}
            location={location}
            searchRadius={searchRadius}
            searchMode={searchMode}
            selectedZones={selectedZones}
            keywords={keywords}
            excludeKeywords={excludeKeywords}
            maxResults={maxResults}
            language={language}
            countryCode={countryCode}
            onGoToStep={goToStep}
            onBack={() => goToStep(5)}
            onSubmit={handleSubmit}
            isSubmitting={submitting}
          />
        )}
      </div>
    </div>
  );
}
